import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, FilePlus2, Award, ShieldCheck } from "lucide-react";

const CERT_TYPES = ["Experience", "Conduct", "Internship", "Volunteer", "Training", "Achievement", "Recommendation", "Custom"];

export default function Certificates() {
  const { user } = useAuth();
  const [data, setData] = useState({ received: [], issued: [] });
  const [inbox, setInbox] = useState([]);
  const canIssue = user?.role === "organization" || user?.role === "institution" || user?.role === "super_admin";
  const isIndividual = user?.role === "professional";

  const load = async () => {
    const { data } = await api.get("/certificates/mine");
    setData(data);
    if (isIndividual) {
      try {
        const r = await api.get("/certificates/inbox");
        setInbox(r.data.rows || []);
      } catch (e) {}
    }
  };
  useEffect(() => {
    load();
  }, []);

  const act = async (cert_id, action, note) => {
    try {
      await api.post(`/certificates/${cert_id}/action`, { action, note });
      toast.success(`Certificate ${action}ed`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="min-h-screen bg-ec-bg" data-testid="certificates-page">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-10">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-8">
          <div>
            <div className="eyebrow">Certificates</div>
            <h1 className="font-display text-4xl font-black tracking-tight mt-2">Verified credentials</h1>
          </div>
          {canIssue && <IssueDialog onIssued={load} />}
        </div>

        <Tabs defaultValue={isIndividual && inbox.length > 0 ? "inbox" : "received"} data-testid="cert-tabs">
          <TabsList className="bg-ec-mute rounded-sm">
            {isIndividual && (
              <TabsTrigger value="inbox" className="rounded-sm" data-testid="cert-tab-inbox">
                Inbox{inbox.length > 0 && <span className="ml-2 bg-ec-emerald text-white text-[10px] px-1.5 py-0.5 rounded-sm font-mono-plex" data-testid="inbox-badge">{inbox.length}</span>}
              </TabsTrigger>
            )}
            <TabsTrigger value="received" className="rounded-sm" data-testid="cert-tab-received">Accepted ({data.received.filter(c => c.acceptance_status !== "Pending" && c.acceptance_status !== "Rejected").length})</TabsTrigger>
            {canIssue && <TabsTrigger value="issued" className="rounded-sm" data-testid="cert-tab-issued">Issued ({data.issued.length})</TabsTrigger>}
          </TabsList>

          {isIndividual && (
            <TabsContent value="inbox" className="mt-6">
              <InboxList items={inbox} onAct={act} />
            </TabsContent>
          )}

          <TabsContent value="received" className="mt-6">
            <CertGrid certs={data.received.filter(c => c.acceptance_status !== "Pending" && c.acceptance_status !== "Rejected")} kind="received" />
          </TabsContent>
          {canIssue && (
            <TabsContent value="issued" className="mt-6">
              <CertGrid certs={data.issued} kind="issued" onRevoked={load} />
            </TabsContent>
          )}
        </Tabs>
      </div>
      <Footer />
    </div>
  );
}

function InboxList({ items, onAct }) {
  if (items.length === 0) {
    return (
      <Card className="rounded-sm border-ec-line">
        <CardContent className="py-16 text-center text-ec-ink-soft" data-testid="inbox-empty">
          Your inbox is empty. Pending certificates will appear here for you to accept, reject, or dispute.
        </CardContent>
      </Card>
    );
  }
  return (
    <div className="space-y-4">
      {items.map((c) => (
        <Card key={c.id} className="rounded-sm border-ec-line" data-testid={`inbox-item-${c.id}`}>
          <CardContent className="p-5 flex flex-col sm:flex-row gap-4 sm:items-center">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className="rounded-sm bg-amber-500/10 text-amber-700 border border-amber-500/40">Pending Acceptance</Badge>
                <Badge className="rounded-sm bg-ec-emerald/10 text-ec-emerald border border-ec-emerald/30">{c.cert_type}</Badge>
                <span className="text-xs text-ec-ink-soft font-mono-plex">{c.certificate_no}</span>
              </div>
              <h3 className="font-display text-lg font-bold mt-2">{c.title}</h3>
              <p className="text-sm text-ec-ink-soft mt-1 line-clamp-2">{c.description}</p>
              <div className="text-xs text-ec-ink-soft mt-2">
                Issued by <span className="font-semibold text-ec-ink">{c.issuer_org}</span> · {c.issue_date}
              </div>
            </div>
            <div className="flex gap-2 sm:flex-col">
              <Button size="sm" className="rounded-sm bg-ec-emerald hover:bg-ec-emerald/90 gap-1 flex-1" onClick={() => onAct(c.id, "accept")} data-testid={`inbox-accept-${c.id}`}>
                <ShieldCheck className="w-3 h-3" /> Accept
              </Button>
              <Button size="sm" variant="outline" className="rounded-sm border-ec-error text-ec-error gap-1 flex-1" onClick={() => {
                const note = prompt("Reason for rejection (optional):") || "";
                onAct(c.id, "reject", note);
              }} data-testid={`inbox-reject-${c.id}`}>
                Reject
              </Button>
              <Button size="sm" variant="outline" className="rounded-sm gap-1 flex-1" onClick={() => {
                const note = prompt("Why are you disputing this certificate?") || "";
                if (note) onAct(c.id, "dispute", note);
              }} data-testid={`inbox-dispute-${c.id}`}>
                Dispute
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function CertGrid({ certs, kind, onRevoked }) {
  if (certs.length === 0) {
    return (
      <Card className="rounded-sm border-ec-line">
        <CardContent className="py-16 text-center text-ec-ink-soft">
          No certificates yet. {kind === "received" ? "When organizations issue you a credential, it will appear here." : "Issue your first certificate to get started."}
        </CardContent>
      </Card>
    );
  }
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {certs.map((c) => <CertCard key={c.id} c={c} onRevoked={onRevoked} kind={kind} />)}
    </div>
  );
}

function CertCard({ c, kind, onRevoked }) {
  const verifyHref = `/verify/${c.id}`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(`${window.location.origin}/verify/${c.id}`)}`;

  const revoke = async () => {
    if (!confirm("Revoke this certificate? The recipient's record will show it as revoked.")) return;
    try {
      await api.post(`/certificates/${c.id}/revoke`);
      toast.success("Certificate revoked");
      onRevoked && onRevoked();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <Card className={`rounded-sm border-ec-line noise-overlay relative overflow-hidden ${c.revoked ? "opacity-60" : ""}`} data-testid={`cert-card-${c.id}`}>
      <div className="absolute inset-0 pointer-events-none opacity-[0.03] text-[80px] font-display font-black flex items-center justify-center select-none">
        ETHICCRED
      </div>
      <CardHeader className="relative border-b border-dashed border-ec-ink/15">
        <div className="flex justify-between items-start">
          <div>
            <Badge className="bg-ec-emerald/10 text-ec-emerald border-ec-emerald/30 mb-2 rounded-sm">{c.cert_type}</Badge>
            <CardTitle className="font-display text-lg leading-tight">{c.title}</CardTitle>
            <div className="text-xs text-ec-ink-soft mt-1 font-mono-plex">{c.certificate_no}</div>
          </div>
          {c.revoked && <span className="stamp text-ec-error text-[10px]">Revoked</span>}
        </div>
      </CardHeader>
      <CardContent className="relative pt-4">
        <p className="text-sm text-ec-ink-soft leading-relaxed line-clamp-3">{c.description}</p>
        <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
          <div>
            <div className="eyebrow">Issued by</div>
            <div className="mt-1 font-semibold">{c.issuer_org}</div>
          </div>
          <div>
            <div className="eyebrow">Recipient</div>
            <div className="mt-1 font-semibold">{kind === "received" ? "You" : c.recipient_name}</div>
          </div>
          <div>
            <div className="eyebrow">Recipient ID</div>
            <div className="mt-1 font-mono-plex font-bold">{c.recipient_ethic_id}</div>
          </div>
          <div>
            <div className="eyebrow">Issue date</div>
            <div className="mt-1 font-semibold">{c.issue_date}</div>
          </div>
        </div>
        <div className="mt-4 flex items-center gap-3 border-t border-ec-line pt-4">
          <img src={qrUrl} alt="QR" className="w-20 h-20 border border-ec-line" />
          <div className="flex-1 text-xs">
            <div className="eyebrow">Verify</div>
            <a className="text-ec-emerald font-semibold underline underline-offset-2" href={verifyHref} data-testid={`cert-verify-link-${c.id}`}>
              Open public link
            </a>
            <div className="font-mono-plex text-ec-ink-soft mt-1 break-all">{`/verify/${c.id}`}</div>
          </div>
        </div>
        {c.conduct_ratings && c.conduct_ratings.length > 0 && (
          <div className="mt-4 border-t border-ec-line pt-4">
            <div className="eyebrow mb-2">Conduct ratings ({c.conduct_ratings.length})</div>
            <div className="flex flex-wrap gap-1.5">
              {c.conduct_ratings.slice(0, 6).map((r) => (
                <span key={r.category} className="text-[10px] font-semibold bg-ec-mute border border-ec-line px-2 py-0.5 rounded-sm">
                  {r.category}: <span className="text-ec-emerald">{r.rating}</span>
                </span>
              ))}
              {c.conduct_ratings.length > 6 && (
                <span className="text-[10px] text-ec-ink-soft">+{c.conduct_ratings.length - 6} more</span>
              )}
            </div>
          </div>
        )}
        {kind === "issued" && !c.revoked && (
          <Button variant="outline" className="rounded-sm border-ec-error text-ec-error mt-4 w-full" onClick={revoke} data-testid={`cert-revoke-${c.id}`}>
            Revoke certificate
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function IssueDialog({ onIssued }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ recipient_ethic_id: "", cert_type: "Experience", title: "", description: "", duration: "", issue_date: "" });
  const [conductRatings, setConductRatings] = useState({}); // { category: rating }
  const [meta, setMeta] = useState({ categories: [], ratings: [] });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/meta/conduct-categories").then(({ data }) => setMeta(data)).catch(() => {});
  }, []);

  const isConduct = f.cert_type === "Conduct";

  const toggleCategory = (cat) => {
    setConductRatings((prev) => {
      const next = { ...prev };
      if (next[cat]) delete next[cat];
      else next[cat] = "Good";
      return next;
    });
  };

  const submit = async () => {
    setLoading(true);
    try {
      const payload = { ...f };
      if (isConduct) {
        payload.conduct_ratings = Object.entries(conductRatings).map(([category, rating]) => ({ category, rating }));
      }
      await api.post("/certificates/issue", payload);
      toast.success("Certificate issued");
      setOpen(false);
      setF({ recipient_ethic_id: "", cert_type: "Experience", title: "", description: "", duration: "", issue_date: "" });
      setConductRatings({});
      onIssued && onIssued();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="rounded-sm bg-ec-ink hover:bg-zinc-800 gap-2" data-testid="issue-certificate-trigger">
          <FilePlus2 className="w-4 h-4" /> Issue certificate
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Award className="w-4 h-4 text-ec-emerald" /> Issue a verified certificate
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Field label="Recipient EthicCred ID">
            <Input
              placeholder="EC-XXXX-XXXX"
              value={f.recipient_ethic_id}
              onChange={(e) => setF({ ...f, recipient_ethic_id: e.target.value.toUpperCase() })}
              data-testid="issue-recipient-input"
            />
          </Field>
          <Field label="Certificate type">
            <Select value={f.cert_type} onValueChange={(v) => setF({ ...f, cert_type: v })}>
              <SelectTrigger className="rounded-sm" data-testid="issue-type-trigger"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CERT_TYPES.map((t) => <SelectItem key={t} value={t} data-testid={`issue-type-${t.toLowerCase()}`}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Title"><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="e.g. Senior Software Engineer" data-testid="issue-title-input" /></Field>
          <Field label="Description"><Textarea rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="issue-description-input" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Duration"><Input value={f.duration} onChange={(e) => setF({ ...f, duration: e.target.value })} placeholder="e.g. 2y 4m" /></Field>
            <Field label="Issue date"><Input type="date" value={f.issue_date} onChange={(e) => setF({ ...f, issue_date: e.target.value })} /></Field>
          </div>

          {isConduct && (
            <div className="border border-ec-line bg-ec-soft p-4 rounded-sm" data-testid="conduct-ratings-section">
              <div className="eyebrow">Conduct categories & ratings</div>
              <p className="text-xs text-ec-ink-soft mt-1">Select categories that apply. Each selected category gets a rating; overall conduct score is computed automatically.</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {meta.categories.map((cat) => {
                  const active = !!conductRatings[cat];
                  return (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => toggleCategory(cat)}
                      className={`text-xs px-2.5 py-1 rounded-sm border transition-colors ${active ? "bg-ec-ink text-white border-ec-ink" : "bg-white border-ec-line hover:border-ec-ink"}`}
                      data-testid={`conduct-cat-${cat.toLowerCase().replace(/ /g, "-")}`}
                    >
                      {cat}
                    </button>
                  );
                })}
              </div>
              {Object.keys(conductRatings).length > 0 && (
                <div className="mt-4 space-y-2 max-h-60 overflow-y-auto">
                  {Object.entries(conductRatings).map(([cat, rating]) => (
                    <div key={cat} className="flex items-center justify-between gap-3 bg-white border border-ec-line px-3 py-2">
                      <span className="text-sm font-semibold">{cat}</span>
                      <Select value={rating} onValueChange={(v) => setConductRatings({ ...conductRatings, [cat]: v })}>
                        <SelectTrigger className="rounded-sm w-44 h-8" data-testid={`conduct-rating-${cat.toLowerCase().replace(/ /g, "-")}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {meta.ratings.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={loading} className="rounded-sm bg-ec-ink hover:bg-zinc-800 gap-2" data-testid="issue-submit-button">
            <ShieldCheck className="w-4 h-4" /> {loading ? "Issuing…" : "Sign & Issue"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="eyebrow">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
